import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { POModal, PoModalModifiers, Vendor } from 'api/types';
import BridgeWaterLogo from 'assets/images/bridgewater-logo.svg';
import LocationIcon from 'assets/images/distance.svg';
import type { DropdownOptionType } from 'components/inputs/Dropdown';
import DropDown from 'components/NewLayout/Dropdown';
import DataTable from 'components/NewLayout/Table';
import CustomTextfield from 'components/NewLayout/Textfield';
import { useEffect } from 'react';
import { dateFormatter, roundToTwoDecimalPlaces, safeParseFloat } from 'utils';
import { paymentMethods } from 'utils/constants';
import type { DataTableObjectType } from 'utils/types';

export const POColumns: DataTableObjectType<POModal>[] = [
  {
    name: 'Item',
    prop: 'item_name',
    data: 'item_name'
  },
  {
    name: 'Description',
    prop: 'description',
    data: 'description'
  },
  {
    name: 'Quantity',
    prop: 'quantity',
    data: (row) => <>{`${row?.quantity ?? '0'}`}</>
  },
  {
    name: 'Unit Price',
    prop: 'price',
    data: (row) => {
      const price = parseFloat(row.price ?? '0');
      return <>{`$${isNaN(price) ? '0.00' : roundToTwoDecimalPlaces(price)}`}</>;
    }
  },
  {
    name: 'Total',
    prop: 'total',
    data: (row) => {
      const price = parseFloat(row?.price ?? '0');
      const quantity = Number(row?.quantity) || 0;
      const total = price * quantity;
      return <>{`$${isNaN(total) ? '0.00' : roundToTwoDecimalPlaces(total)}`}</>;
    }
  }
];

const formatDateForInput = (date: Date | string): string => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function PoData({
  data,
  vendorBillingData,
  deliveryAddress,
  specialInstructions,
  setSpecialInstructions,
  currentDate,
  poNum,
  poModifiers,
  poDate,
  purchaseDate,
  setPurchaseDate,
  tableId,
  paymentTerms,
  setPaymentTerms,
  allCreditCards,
  selectedCreditCard,
  setCreditCard,
  requester,
  setPoModalData
}: {
  data: POModal[];
  vendorBillingData?: Vendor;
  deliveryAddress: string;
  specialInstructions: string;
  setSpecialInstructions?: (res) => void;
  currentDate?: any;
  poNum?: string;
  poModifiers?: PoModalModifiers;
  poDate?: string;
  purchaseDate: string;
  setPurchaseDate?: (value: string) => void;
  tableId: string;
  paymentTerms: string;
  setPaymentTerms?: (value: string) => void;
  allCreditCards?: DropdownOptionType[];
  selectedCreditCard?: string;
  setCreditCard?: (value: string) => void;
  requester: string;
  setPoModalData?: React.Dispatch<React.SetStateAction<PoModalModifiers>>;
}) {
  let total = 0;
  data.forEach((d) => {
    const price = safeParseFloat(d?.price);
    const quantity = safeParseFloat(d?.quantity);
    total += price * quantity;
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (setPurchaseDate && (!purchaseDate || !purchaseDate.match(/^\d{4}-\d{2}-\d{2}$/))) {
      const defaultDate = formatDateForInput(new Date());
      setPurchaseDate(defaultDate);
    }
  }, [purchaseDate, setPurchaseDate]);

  const formattedPurchaseDate =
    purchaseDate && purchaseDate.match(/^\d{4}-\d{2}-\d{2}$/)
      ? purchaseDate
      : formatDateForInput(new Date());

  return (
    <Grid container spacing={2} minWidth="auto" id="po-card-data">
      <Grid item xs={6}>
        <img src={BridgeWaterLogo} width={isMobile ? '150px' : '200px'} />
        <Typography component="div" sx={{ fontSize: '20px', color: '#4E4E4E' }}>
          Bridgewater Studio Inc.
        </Typography>
      </Grid>
      <Grid item xs={6} textAlign="center">
        {' '}
        <Typography
          component="div"
          sx={{ fontSize: isMobile ? '18px' : '32px', fontWeight: '700' }}>
          Purchase Order
        </Typography>
        <Typography component="div">
          <img src={LocationIcon} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
          4834 S Oakley Ave, Chicago, IL 60609 | (312) 702-1335
        </Typography>
        <Typography component="div" sx={{ mb: 3 }}>
          <Typography component="div" sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography sx={{ fontWeight: 'bold', width: '150px', textAlign: 'left' }}>
              PO Number:
            </Typography>
            <Typography sx={{ width: '100px', textAlign: 'left' }}>
              {poNum ? poNum : '00000'}
            </Typography>
          </Typography>
          <Typography component="div" sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography sx={{ fontWeight: 'bold', width: '150px', textAlign: 'left' }}>
              PO Date:
            </Typography>
            <Typography sx={{ width: '100px', textAlign: 'left' }}>
              {poDate ? dateFormatter(poDate, 'date') : currentDate}
            </Typography>
          </Typography>
          <Typography component="div" sx={{ display: 'flex', justifyContent: 'center', ml: 6 }}>
            <Typography sx={{ fontWeight: 'bold', width: '150px', textAlign: 'left' }}>
              Purchase Date:
            </Typography>
            {setPurchaseDate ? (
              <Box sx={{ width: '150px', textAlign: 'left' }}>
                <CustomTextfield
                  name="purchase_date"
                  type="date"
                  size="small"
                  value={formattedPurchaseDate}
                  onChange={(event) => setPurchaseDate(event.target.value)}
                />
              </Box>
            ) : (
              <Typography sx={{ width: '100px', textAlign: 'left', mr: 6 }}>
                {purchaseDate ? dateFormatter(purchaseDate, 'date') : ''}
              </Typography>
            )}
          </Typography>
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Typography
          component="div"
          sx={{
            display: 'flex',
            mb: 1
          }}>
          <Typography
            component="div"
            sx={{
              textAlign: 'left',
              p: 3,
              width: isMobile ? '100%' : '360px',
              background: '#f4f4f4',
              borderRadius: '20px'
            }}>
            <Typography component="div" sx={{ fontSize: '18px', fontWeight: '600', mb: 1 }}>
              Vendor Details
            </Typography>
            <Typography sx={{ mb: 1 }}>
              <Typography component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                Company Name:
              </Typography>
              {vendorBillingData?.vendor_name || ''}
            </Typography>
            <Typography sx={{ mb: 1 }}>
              <Typography component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                Billing Address:
              </Typography>
              <Typography component="span">
                {vendorBillingData
                  ? [
                      vendorBillingData.billed_from_address1,
                      vendorBillingData.billed_from_city,
                      vendorBillingData.billed_from_state,
                      vendorBillingData.billed_from_postal_code,
                      vendorBillingData.billed_from_country
                    ]
                      .filter(Boolean)
                      .join(', ')
                  : ''}
              </Typography>
            </Typography>
            <Typography sx={{ mb: 1 }}>
              <Typography component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                Phone:
              </Typography>
              {vendorBillingData?.phone_number || ''}
            </Typography>
          </Typography>
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} textAlign="right">
        <Typography
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'row-reverse',
            mb: 1
          }}>
          <Typography
            component="div"
            sx={{
              textAlign: 'left',
              p: 3,
              width: isMobile ? '100%' : '360px',
              background: '#f4f4f4',
              borderRadius: '20px'
            }}>
            <Typography component="div" sx={{ fontSize: '18px', fontWeight: '600', mb: 1 }}>
              Delivery Details
            </Typography>
            <Typography sx={{ mb: 1 }}>
              <img src={LocationIcon} width="15px" style={{ marginRight: '10px' }} />
              {deliveryAddress}
            </Typography>
            {setPaymentTerms ? (
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                <Typography component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                  Payment Method:
                </Typography>
                <Box sx={{ width: '60%' }}>
                  <DropDown
                    label=""
                    options={paymentMethods}
                    value={paymentTerms}
                    inputName="payment_terms"
                    size="small"
                    onChange={(e) => setPaymentTerms(e.target.value)}
                  />
                </Box>
              </Box>
            ) : (
              <Typography sx={{ mb: 1 }}>
                <Typography component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                  Payment Method:
                </Typography>
                {paymentTerms}
              </Typography>
            )}

            {paymentTerms == 'Credit Card' &&
              (setCreditCard ? (
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                    Credit Card:
                  </Typography>
                  <Box sx={{ width: '60%' }}>
                    <DropDown
                      label=""
                      options={allCreditCards || []}
                      value={selectedCreditCard || ''}
                      inputName="credit_card"
                      size="small"
                      onChange={(e) => setCreditCard(e.target.value)}
                    />
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ mb: 1 }}>
                  <Typography component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                    Credit Card:
                  </Typography>
                  {selectedCreditCard}
                </Typography>
              ))}

            <Typography sx={{ mb: 1 }}>
              <Typography component="span" sx={{ fontWeight: 'bold', mr: 1 }}>
                Requested By:
              </Typography>
              {requester}
            </Typography>
          </Typography>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <DataTable
          items={data}
          columns={POColumns}
          loading={false}
          hidePagination
          rowsPerPage={Number(data?.length)}
          tableHeight="40vh"
        />
      </Grid>
      <Grid item xs={6} sx={{ mt: 3 }}>
        <Typography component="div" sx={{ fontSize: '18px', fontWeight: '500', mb: 1 }}>
          Special Instructions
        </Typography>
        <CustomTextfield
          labelText=""
          name="special-instruction"
          isTextarea
          value={specialInstructions || ''}
          disabled={!setSpecialInstructions}
          onChange={(e) => setSpecialInstructions && setSpecialInstructions(e.target.value)}
        />
        <Typography component="div" sx={{ fontSize: '18px', fontWeight: '500', mb: 1 }}>
          Authorized
        </Typography>
        <CustomTextfield labelText="" name="authorized" disabled />
      </Grid>
      <Grid item xs={6} sx={{ position: 'relative' }}>
        <Typography
          component="div"
          sx={{
            p: 3,
            background: '#f4f4f4',
            borderRadius: '20px',
            position: 'absolute',
            width: '97%',
            bottom: '15px',
            left: '15px',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr'
          }}>
          <Typography component="div" sx={{ textAlign: 'left' }}>
            <Typography>Total Net:</Typography>
            <Typography>Shipping:</Typography>
            <Typography>Taxes:</Typography>
            <Typography>Discount:</Typography>
            <Typography>Deposit:</Typography>
            <Typography>Refund:</Typography>
            <Typography sx={{ mt: 2, fontSize: '24px' }}>TOTAL:</Typography>
          </Typography>
          <Typography component="div" sx={{ textAlign: 'right' }}>
            <Typography>${roundToTwoDecimalPlaces(total || '0.00')}</Typography>
            <Typography>
              ${roundToTwoDecimalPlaces(poModifiers?.shipping_cost || '0.00')}{' '}
            </Typography>
            <Typography>
              ${roundToTwoDecimalPlaces(poModifiers?.estimated_taxes || '0.00')}
            </Typography>
            <Typography>${roundToTwoDecimalPlaces(poModifiers?.discount || '0.00')}</Typography>
            <Typography>${roundToTwoDecimalPlaces(poModifiers?.deposit || '0.00')}</Typography>
            <Typography>${roundToTwoDecimalPlaces(poModifiers?.refund || '0.00')}</Typography>
            <Typography sx={{ mt: 2, fontSize: '24px' }}>
              $
              {roundToTwoDecimalPlaces(
                total +
                  parseFloat(poModifiers?.shipping_cost || '0') +
                  parseFloat(poModifiers?.estimated_taxes || '0') -
                  parseFloat(poModifiers?.discount || '0') -
                  parseFloat(poModifiers?.deposit || '0') -
                  parseFloat(poModifiers?.refund || '0')
              )}
            </Typography>
          </Typography>
        </Typography>
      </Grid>
    </Grid>
  );
}
